<template>
  <ValidationObserver ref="observer">
    <v-form class="mobile-quick-create-form" @submit.prevent="submit">
      <section class="mobile-quick-create-form__section" data-testid="work-order-section">
        <button
          type="button"
          class="mobile-quick-create-form__section-heading mobile-quick-create-form__section-toggle"
          @click="workOrderOpen = !workOrderOpen"
        >
          <span
            class="mobile-quick-create-form__section-icon mobile-quick-create-form__section-icon--work"
          >
            <v-icon size="19" color="#0f766e">mdi-clipboard-text-outline</v-icon>
          </span>
          <h2 class="mobile-quick-create-form__section-title">
            {{ $t('mobile_quick_create_work_order_section') }}
          </h2>
          <v-icon class="ml-auto">{{
            workOrderOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'
          }}</v-icon>
        </button>

        <div v-show="workOrderOpen">
          <ValidationProvider
            :name="$t('mobile_quick_create_customer')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-autocomplete
              v-model="form.customerId"
              :items="customerItems"
              :search-input.sync="customerSearch"
              :loading="customerLoading"
              :loading-text="$t('mobile_quick_create_loading')"
              :no-data-text="$t('mobile_quick_create_no_data')"
              :item-text="customerItemText"
              item-value="Id"
              :error-messages="[errors[0], customerError].filter(Boolean)"
              :label="$t('mobile_quick_create_customer')"
              :disabled="submissionLocked"
              class="mobile-quick-create-form__control mb-2"
              prepend-inner-icon="mdi-domain"
              outlined
              dense
              hide-details="auto"
              no-filter
              clearable
              @focus="loadInitialCustomers"
              @change="onCustomerChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.Number }}</v-list-item-title>
                  <v-list-item-subtitle
                    >{{ $t('mobile_quick_create_short_name') }} :
                    {{ item.ShortName }}</v-list-item-subtitle
                  >
                  <v-list-item-subtitle
                    >{{ $t('mobile_quick_create_full_name') }} :
                    {{ item.FullName }}</v-list-item-subtitle
                  >
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </ValidationProvider>

          <div v-if="selectedCustomer" class="mobile-quick-create-form__customer-summary">
            <div class="mobile-quick-create-form__customer-heading">
              <span>{{ selectedCustomer.Number }}</span>
              <strong>{{ form.shortName }}</strong>
            </div>
            <div class="mobile-quick-create-form__customer-name">{{ form.fullName }}</div>
          </div>

          <div class="mobile-quick-create-form__field-grid">
            <ValidationProvider
              :name="$t('mobile_quick_create_subject')"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="form.subject"
                :label="$t('mobile_quick_create_subject')"
                :error-messages="errors[0]"
                :disabled="submissionLocked"
                class="mobile-quick-create-form__control"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </ValidationProvider>
            <ValidationProvider
              :name="$t('mobile_quick_create_license_plate')"
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="form.licensePlate"
                :label="$t('mobile_quick_create_license_plate')"
                :error-messages="errors[0]"
                :disabled="submissionLocked"
                class="mobile-quick-create-form__control"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </ValidationProvider>
          </div>

          <ValidationProvider
            :name="$t('mobile_quick_create_reason')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-textarea
              v-model="form.fixReason"
              :label="$t('mobile_quick_create_reason')"
              :error-messages="errors[0]"
              rows="2"
              auto-grow
              :disabled="submissionLocked"
              class="mobile-quick-create-form__control"
              outlined
              dense
              hide-details="auto"
            ></v-textarea>
          </ValidationProvider>
        </div>
      </section>

      <section class="mobile-quick-create-form__section">
        <button
          type="button"
          class="mobile-quick-create-form__section-heading mobile-quick-create-form__section-toggle"
          @click="workItemsOpen = !workItemsOpen"
        >
          <span
            class="mobile-quick-create-form__section-icon mobile-quick-create-form__section-icon--work"
          >
            <v-icon size="19" color="#0f766e">mdi-wrench-outline</v-icon>
          </span>
          <h2 class="mobile-quick-create-form__section-title">
            {{ $t('mobile_quick_create_work_items') }}
          </h2>
          <v-icon class="ml-auto">{{
            workItemsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'
          }}</v-icon>
        </button>

        <div v-show="workItemsOpen">
          <div class="mobile-quick-create-form__hint">
            {{ $t('mobile_quick_create_serial_lost_hint') }}
          </div>
          <div
            v-for="(line, index) in workItemLines"
            :key="line.id"
            class="mobile-quick-create-form__item-card"
          >
            <button
              type="button"
              class="mobile-quick-create-form__item-card-head"
              :class="{
                'mobile-quick-create-form__item-card-head--remove': line.kind === 'remove',
                'mobile-quick-create-form__item-card-head--install': line.kind !== 'remove',
              }"
              @click="line.open = !line.open"
            >
              <span>{{ lineTitle(line, index) }}</span>
              <v-icon>{{ line.open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </button>
            <div v-show="line.open" class="mobile-quick-create-form__item-card-body">
              <template v-if="line.kind === 'remove'">
                <div class="mobile-quick-create-form__item-label">
                  {{ $t('mobile_quick_create_remove') }}
                </div>
                <v-text-field
                  v-if="line.removeItemId"
                  :value="line.removeName"
                  :label="$t('mobile_quick_create_item_number')"
                  outlined
                  dense
                  hide-details="auto"
                  readonly
                  clearable
                  class="mobile-quick-create-form__control mb-2"
                  :disabled="submissionLocked"
                  @click:clear="clearLineRemoveItem(line)"
                ></v-text-field>
                <item-autocomplete-for-eg
                  v-else
                  :fatherEditedItem="removeItemView(line)"
                  :licensePlate="form.licensePlate"
                  :editedItemIndex="-1"
                  :itemName="'quotation_item_number'"
                  :customerDeviceType="line.removeCustomerDeviceType || 0"
                  :isAllTextFieldDisable="submissionLocked"
                  @setEditedItem="(item) => setLineRemoveItem(line, item)"
                />
                <v-text-field
                  v-model="line.removeSerial"
                  :label="$t('mobile_quick_create_remove_serial')"
                  append-icon="mdi-qrcode-scan"
                  outlined
                  dense
                  hide-details="auto"
                  class="mobile-quick-create-form__control mb-2"
                  :disabled="submissionLocked || scanning"
                  @click:append="openLineScanner(line, 'removeSerial')"
                  @blur="lookupSerial(line, 'removeSerial')"
                ></v-text-field>
                <div
                  v-if="serialError"
                  class="red--text text--darken-3 mt-1"
                  style="font-size: 0.8rem;"
                >
                  {{ serialError }}
                </div>
                <v-text-field
                  v-model="line.removeQty"
                  :label="$t('mobile_quick_create_remove_qty')"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  class="mobile-quick-create-form__control mb-2"
                  :disabled="submissionLocked"
                ></v-text-field>
              </template>

              <template v-else>
                <div class="mobile-quick-create-form__item-label">
                  {{ $t('mobile_quick_create_install') }}
                </div>
                <v-text-field
                  v-if="line.returnItemId"
                  :value="line.returnName"
                  :label="$t('mobile_quick_create_item_number')"
                  outlined
                  dense
                  hide-details="auto"
                  readonly
                  clearable
                  class="mobile-quick-create-form__control mb-2"
                  :disabled="submissionLocked"
                  @click:clear="clearLineReturnItem(line)"
                ></v-text-field>
                <item-autocomplete-for-eg
                  v-else
                  :fatherEditedItem="returnItemView(line)"
                  :licensePlate="form.licensePlate"
                  :editedItemIndex="-1"
                  :itemName="'quotation_item_number'"
                  :customerDeviceType="line.returnCustomerDeviceType || 0"
                  :isAllTextFieldDisable="submissionLocked"
                  @setEditedItem="(item) => setLineReturnItem(line, item)"
                />
                <v-text-field
                  v-model="line.returnSerial"
                  :label="$t('mobile_quick_create_return_serial')"
                  append-icon="mdi-qrcode-scan"
                  outlined
                  dense
                  hide-details="auto"
                  class="mobile-quick-create-form__control mb-2"
                  :disabled="submissionLocked || scanning"
                  @click:append="openLineScanner(line, 'returnSerial')"
                  @blur="lookupSerial(line, 'returnSerial')"
                ></v-text-field>
                <div
                  v-if="serialError"
                  class="red--text text--darken-3 mt-1"
                  style="font-size: 0.8rem;"
                >
                  {{ serialError }}
                </div>
                <v-text-field
                  v-model="line.returnQty"
                  :label="$t('mobile_quick_create_return_qty')"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  class="mobile-quick-create-form__control mb-2"
                  :disabled="submissionLocked"
                ></v-text-field>
              </template>

              <v-btn
                type="button"
                text
                color="error"
                small
                class="ml-n2"
                :disabled="submissionLocked"
                @click="removeWorkItemLine(index)"
              >
                {{ $t('mobile_quick_create_delete_line') }}
              </v-btn>
            </div>
          </div>

          <div class="mobile-quick-create-form__item-actions">
            <v-btn
              type="button"
              text
              class="mobile-quick-create-form__optional-toggle"
              :disabled="submissionLocked"
              @click="addWorkItemLine('remove')"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('mobile_quick_create_add_remove') }}
            </v-btn>
            <v-btn
              type="button"
              text
              class="mobile-quick-create-form__optional-toggle"
              :disabled="submissionLocked"
              @click="addWorkItemLine('install')"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('mobile_quick_create_add_install') }}
            </v-btn>
          </div>
        </div>
      </section>

      <section class="mobile-quick-create-form__section">
        <button
          type="button"
          class="mobile-quick-create-form__section-heading mobile-quick-create-form__section-toggle"
          @click="toggleSignature"
        >
          <span
            class="mobile-quick-create-form__section-icon mobile-quick-create-form__section-icon--photo"
          >
            <v-icon size="19" color="#b45309">mdi-draw</v-icon>
          </span>
          <h2 class="mobile-quick-create-form__section-title">
            {{ $t('mobile_quick_create_signature') }}
          </h2>
          <v-icon class="ml-auto">{{
            signatureOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'
          }}</v-icon>
        </button>

        <div v-show="signatureOpen">
          <div v-if="signaturePhoto" class="mobile-quick-create-form__signature-preview">
            <img
              :src="signaturePhoto.previewUrl"
              :alt="$t('mobile_quick_create_signature_title')"
              @click="signatureViewerOpen = true"
            />
            <div class="mobile-quick-create-form__signature-status">
              <span v-if="signaturePhoto.status === photoStatus.UPLOADING">{{
                $t('mobile_quick_create_signature_uploading')
              }}</span>
              <span
                v-else-if="signaturePhoto.status === photoStatus.SUCCESS"
                class="green--text text--darken-3"
              >
                {{ $t('mobile_quick_create_signature_success_zoom') }}
              </span>
              <span v-else class="red--text text--darken-3">{{
                $t('mobile_quick_create_signature_failed')
              }}</span>
            </div>
            <v-btn
              type="button"
              text
              small
              color="error"
              :disabled="submissionLocked || signaturePhoto.deleting"
              @click="clearSignature"
            >
              {{ $t('mobile_quick_create_resign') }}
            </v-btn>
          </div>
          <template v-else>
            <canvas
              ref="signaturePad"
              class="mobile-quick-create-form__signature-pad"
              @touchstart.prevent="startSign"
              @touchmove.prevent="drawSign"
              @touchend.prevent="endSign"
              @mousedown.prevent="startSign"
              @mousemove.prevent="drawSign"
              @mouseup.prevent="endSign"
              @mouseleave.prevent="endSign"
            ></canvas>
            <div
              v-if="signatureDirty"
              class="red--text text--darken-3 mt-2"
              style="font-size: 0.875rem;"
            >
              {{ $t('mobile_quick_create_signature_dirty') }}
            </div>
            <div class="mobile-quick-create-form__item-actions">
              <v-btn type="button" text small @click="clearPad">{{
                $t('mobile_quick_create_clear')
              }}</v-btn>
              <v-btn
                type="button"
                text
                small
                color="primary"
                :loading="signing"
                :disabled="submissionLocked"
                @click="confirmSignature"
              >
                {{ $t('mobile_quick_create_confirm_signature') }}
              </v-btn>
            </div>
          </template>
        </div>
      </section>

      <section class="mobile-quick-create-form__section mobile-quick-create-form__photos">
        <div class="mobile-quick-create-form__section-heading">
          <span
            class="mobile-quick-create-form__section-icon mobile-quick-create-form__section-icon--photo"
          >
            <v-icon size="19" color="#b45309">mdi-camera-outline</v-icon>
          </span>
          <h2 class="mobile-quick-create-form__section-title">
            {{ $t('mobile_quick_create_photos') }}
          </h2>
          <div class="mobile-quick-create-form__photo-count">
            {{ $t('mobile_quick_create_photo_success_count', { count: successfulPhotoCount }) }}
          </div>
        </div>
        <v-alert v-if="photoError" type="error" dense class="mb-2">{{ photoError }}</v-alert>
        <v-list v-if="photos.length" dense class="mobile-quick-create-form__photo-list pa-0 mb-2">
          <v-list-item v-for="photo in photos" :key="photo.localId" class="px-0">
            <v-list-item-avatar
              tile
              size="56"
              class="mobile-quick-create-form__photo-thumb"
              @click="openPhotoViewer(photo)"
            >
              <v-img
                v-if="photo.previewUrl"
                :src="photo.previewUrl"
                :alt="photo.displayName"
              ></v-img>
              <v-icon v-else>mdi-image</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-truncate">{{ photo.displayName }}</v-list-item-title>
              <v-list-item-subtitle>
                <span v-if="photo.deleting">{{ $t('mobile_quick_create_photo_removing') }}</span>
                <span v-else-if="photo.status === photoStatus.UPLOADING">
                  {{
                    $t('mobile_quick_create_photo_uploading_progress', { progress: photo.progress })
                  }}
                </span>
                <span
                  v-else-if="photo.status === photoStatus.SUCCESS"
                  class="green--text text--darken-3"
                >
                  {{ $t('mobile_quick_create_photo_success_zoom') }}
                </span>
                <span v-else class="red--text text--darken-3">{{
                  $t('mobile_quick_create_photo_failed')
                }}</span>
              </v-list-item-subtitle>
              <v-progress-linear
                v-if="photo.status === photoStatus.UPLOADING"
                :value="photo.progress"
                height="3"
                color="primary"
                class="mt-1"
              ></v-progress-linear>
            </v-list-item-content>
            <v-list-item-action class="d-flex flex-row">
              <v-btn
                v-if="photo.status === photoStatus.FAILURE"
                icon
                large
                type="button"
                :disabled="submissionLocked || photo.deleting"
                @click="retryPhoto(photo)"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                icon
                large
                type="button"
                :loading="photo.deleting"
                :disabled="
                  submissionLocked || photo.deleting || photo.status === photoStatus.UPLOADING
                "
                @click="removePhoto(photo.localId)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <input
          ref="photoInput"
          type="file"
          accept="image/*"
          class="d-none"
          :disabled="submissionLocked || photoLimitReached"
          @change="onPhotoSelected"
        />
        <v-btn
          depressed
          block
          type="button"
          :height="photos.length ? 48 : 96"
          class="mobile-quick-create-form__photo-button"
          :class="{ 'mobile-quick-create-form__photo-button--empty': !photos.length }"
          :disabled="submissionLocked || photoLimitReached"
          @click="openPhotoPicker"
        >
          <v-icon :left="photos.length > 0" :size="photos.length ? 20 : 28"
            >mdi-camera-plus-outline</v-icon
          >
          {{ $t('mobile_quick_create_add_photo') }}
        </v-btn>
      </section>

      <div class="mobile-quick-create-form__actions">
        <div class="mobile-quick-create-form__actions-inner">
          <div v-if="missingLabels.length" class="mobile-quick-create-form__missing">
            <div class="mobile-quick-create-form__missing-title">
              {{ $t('mobile_quick_create_missing_title') }}
            </div>
            <div class="mobile-quick-create-form__missing-list">
              <span
                v-for="item in missingLabels"
                :key="item.key"
                class="mobile-quick-create-form__missing-chip"
                @click="focusMissing(item.key)"
              >
                {{ item.label }}
              </span>
            </div>
          </div>
          <v-btn
            color="#0f766e"
            dark
            block
            large
            type="submit"
            class="mobile-quick-create-form__submit"
            :loading="submitting"
            :disabled="isSubmitDisabled()"
          >
            <v-icon left>mdi-check</v-icon>
            {{ $t('mobile_quick_create_submit') }}
          </v-btn>
        </div>
      </div>
    </v-form>

    <v-dialog v-model="scannerOpen" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-3">
          {{ $t('mobile_quick_create_scan_serial') }}
          <v-spacer></v-spacer>
          <v-btn icon type="button" @click="closeSerialScanner">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div id="serial-reader" class="mobile-quick-create-form__scanner-video"></div>
          <div v-if="scanError" class="red--text mt-2">{{ scanError }}</div>
          <input
            ref="serialImageInput"
            type="file"
            accept="image/*"
            class="d-none"
            @change="onSerialImageSelected"
          />
          <v-btn type="button" depressed block class="mt-3" @click="$refs.serialImageInput.click()">
            <v-icon left>mdi-image</v-icon>
            {{ $t('mobile_quick_create_scan_from_image') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="photoConfirmOpen" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-3">
          {{ $t('mobile_quick_create_confirm_photo') }}
          <v-spacer></v-spacer>
          <v-btn icon type="button" @click="cancelPhotoConfirm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <img
            v-if="pendingPhotoUrl"
            :src="pendingPhotoUrl"
            style="width: 100%; border-radius: 8px;"
            :alt="$t('mobile_quick_create_confirm_photo')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text type="button" @click="cancelPhotoConfirm">
            {{ $t('common_cancel') }}
          </v-btn>
          <v-btn color="primary" type="button" @click="confirmPendingPhoto">
            {{ $t('common_confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="photoViewerOpen" max-width="90%">
      <v-card>
        <v-card-title class="py-3">
          {{ $t('mobile_quick_create_photo_viewer') }}
          <v-spacer></v-spacer>
          <v-btn icon type="button" @click="photoViewerOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <img
            v-if="viewingPhoto && viewingPhoto.previewUrl"
            :src="viewingPhoto.previewUrl"
            style="width: 100%;"
            :alt="viewingPhoto.displayName"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="signatureViewerOpen" max-width="480">
      <v-card>
        <v-card-title class="py-3">
          {{ $t('mobile_quick_create_signature_title') }}
          <v-spacer></v-spacer>
          <v-btn icon type="button" @click="signatureViewerOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <img
            v-if="signaturePhoto"
            :src="signaturePhoto.previewUrl"
            class="mobile-quick-create-form__signature-full"
            :alt="$t('mobile_quick_create_signature_title')"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </ValidationObserver>
</template>

<script>
import ItemAutocompleteForEg from '@/components/ItemAutocompleteForEg.vue'
import { Html5Qrcode } from 'html5-qrcode'
import { HttpCustomer, HttpMobileFix } from '@/js/http-Manager'
import {
  CUSTOMER_SEARCH_DEBOUNCE_MS,
  PHOTO_MAX_COUNT,
  PHOTO_STATUS,
  buildCustomerSearchParams,
  compressImageFile,
  createPhotoItem,
  createRequestId,
  debounce,
  emptyQuickCreateForm,
  hasSuccessfulPhoto,
  isPhotoUploading,
  revokePhotoPreview,
  replacePhotoPreviewWithCompressed,
  shouldSearchCustomers,
  successfulFileIds,
  uploadProgressPercent
} from '@/js/mobile-quick-create'
import { createWorkItemLine, buildEngineerWorkItems } from '@/js/use-work-item-lines'
const CUSTOMER_SEARCH_TIMEOUT_MS = 30000

export default {
  name: 'QuickCreateForm',
  components: {
    ItemAutocompleteForEg
  },
  props: {
    submissionBlocked: { type: Boolean, default: false },
    submitting: { type: Boolean, default: false },
    submissionLocked: { type: Boolean, default: false }
  },
  data () {
    return {
      form: emptyQuickCreateForm(),
      workOrderOpen: true,
      workItemsOpen: false,
      signatureOpen: false,
      signaturePhoto: null,
      signatureViewerOpen: false,
      signing: false,
      drawing: false,
      signatureDirty: false,
      lastPoint: null,
      customers: [],
      initialCustomers: [],
      initialCustomersLoaded: false,
      selectedCustomer: null,
      customerSearch: '',
      customerLoading: false,
      customerError: '',
      customerSearchSequence: 0,
      photos: [],
      photoError: '',
      photoStatus: PHOTO_STATUS,
      searchCustomersDebounced: null,
      scannerOpen: false,
      scanning: false,
      scanError: '',
      html5Qrcode: null,
      workItemLines: [],
      scanTargetLine: null,
      scanTargetField: '',
      photoConfirmOpen: false,
      pendingPhotoFile: null,
      pendingPhotoUrl: '',
      photoViewerOpen: false,
      viewingPhoto: null,
      serialError: ''
    }
  },
  computed: {
    customerItems () {
      if (!this.selectedCustomer) return this.customers
      const exists = this.customers.some((item) => item.Id === this.selectedCustomer.Id)
      return exists ? this.customers : [this.selectedCustomer].concat(this.customers)
    },
    isUploading () {
      return (
        isPhotoUploading(this.photos) ||
        (this.signaturePhoto && this.signaturePhoto.status === PHOTO_STATUS.UPLOADING)
      )
    },
    isDeleting () {
      return (
        this.photos.some((photo) => photo.deleting) ||
        (this.signaturePhoto && this.signaturePhoto.deleting)
      )
    },
    successfulPhotoCount () {
      return successfulFileIds(this.photos).length
    },
    photoLimitReached () {
      const signatureCount = this.signaturePhoto ? 1 : 0
      return this.photos.length + signatureCount >= PHOTO_MAX_COUNT
    },
    missingLabels () {
      const list = []
      if (!this.selectedCustomer || !this.form.customerId) {
        list.push({ key: 'customer', label: this.$t('mobile_quick_create_customer') })
      }
      if (!String(this.form.subject || '').trim()) {
        list.push({ key: 'subject', label: this.$t('mobile_quick_create_subject') })
      }
      if (!String(this.form.licensePlate || '').trim()) {
        list.push({ key: 'licensePlate', label: this.$t('mobile_quick_create_license_plate') })
      }
      if (!String(this.form.fixReason || '').trim()) {
        list.push({ key: 'reason', label: this.$t('mobile_quick_create_reason') })
      }
      if (this.signatureDirty && !this.signaturePhoto) {
        list.push({
          key: 'signature',
          label: this.$t('mobile_quick_create_signature_not_confirmed')
        })
      }
      const incompleteItems = this.workItemLines.filter((line) => {
        if (line.kind === 'remove') {
          if (line.removeItemId && !line.removeHasSerialNumber) return false
          return !String(line.removeSerial || '').trim()
        }
        if (line.returnItemId && !line.returnHasSerialNumber) return false
        return !String(line.returnSerial || '').trim()
      })
      if (incompleteItems.length) {
        list.push({ key: 'workItems', label: this.$t('mobile_quick_create_item_no_serial') })
      }
      if (!hasSuccessfulPhoto(this.photos)) {
        list.push({ key: 'photo', label: this.$t('mobile_quick_create_photos') })
      }
      return list
    }
  },
  created () {
    this.searchCustomersDebounced = debounce(this.searchCustomers, CUSTOMER_SEARCH_DEBOUNCE_MS)
  },
  beforeDestroy () {
    this.closeSerialScanner()
    if (this.searchCustomersDebounced) this.searchCustomersDebounced.cancel()
    this.photos.forEach(revokePhotoPreview)
    if (this.signaturePhoto) revokePhotoPreview(this.signaturePhoto)
    this.clearPendingPhoto()
  },
  watch: {
    customerSearch (val) {
      if (this.selectedCustomer && val !== this.customerItemText(this.selectedCustomer)) {
        this.clearSelectedCustomer()
      }
      const sequence = ++this.customerSearchSequence
      this.customerError = ''
      if (!shouldSearchCustomers(val)) {
        this.searchCustomersDebounced.cancel()
        this.customers = this.initialCustomersLoaded ? this.initialCustomers.slice() : []
        this.customerLoading = false
        return
      }
      this.customers = []
      this.customerLoading = true
      this.searchCustomersDebounced(val, sequence)
    }
  },
  methods: {
    focusMissing (key) {
      if (key === 'photo') {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.mobile-quick-create-form__photos')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        return
      }
      if (key === 'signature') {
        this.signatureOpen = true
        this.$nextTick(() => {
          const el = this.$el.querySelector('.mobile-quick-create-form__signature-pad')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        return
      }
      if (key === 'workItems') {
        this.workItemsOpen = true
        this.$nextTick(() => {
          const el = this.$el.querySelector('.mobile-quick-create-form__item-card')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        return
      }
      this.workOrderOpen = true
      this.$nextTick(() => {
        const el = this.$el.querySelector('[data-testid="work-order-section"]')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    toggleSignature () {
      this.signatureOpen = !this.signatureOpen
      if (this.signatureOpen && !this.signaturePhoto) {
        this.$nextTick(this.resizeSignaturePad)
      }
    },
    resizeSignaturePad () {
      const canvas = this.$refs.signaturePad
      if (!canvas) return
      const ratio = window.devicePixelRatio || 1
      canvas.width = canvas.offsetWidth * ratio
      canvas.height = 180 * ratio
      const ctx = canvas.getContext('2d')
      ctx.scale(ratio, ratio)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.offsetWidth, canvas.height)
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.strokeStyle = '#102a43'
    },
    pointFromEvent (event) {
      const canvas = this.$refs.signaturePad
      const rect = canvas.getBoundingClientRect()
      const source = event.touches ? event.touches[0] : event
      return { x: source.clientX - rect.left, y: source.clientY - rect.top }
    },
    startSign (event) {
      if (this.submissionLocked) return
      this.drawing = true
      this.signatureDirty = true
      this.lastPoint = this.pointFromEvent(event)
    },
    drawSign (event) {
      if (!this.drawing) return
      const canvas = this.$refs.signaturePad
      const ctx = canvas.getContext('2d')
      const point = this.pointFromEvent(event)
      ctx.beginPath()
      ctx.moveTo(this.lastPoint.x, this.lastPoint.y)
      ctx.lineTo(point.x, point.y)
      ctx.stroke()
      this.lastPoint = point
    },
    endSign () {
      this.drawing = false
      this.lastPoint = null
    },
    clearPad () {
      const canvas = this.$refs.signaturePad
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.offsetWidth, canvas.height)
      ctx.strokeStyle = '#102a43'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      this.signatureDirty = false
    },
    async confirmSignature () {
      if (this.photoLimitReached) {
        this.photoError = this.$t('mobile_quick_create_photo_limit')
        return
      }
      const canvas = this.$refs.signaturePad
      if (!canvas || this.submissionLocked) return
      this.signing = true
      try {
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
        if (!blob) return
        const file = new File([blob], 'sign_' + Date.now() + '.png', { type: 'image/png' })
        const photo = createPhotoItem(file, createRequestId())
        this.signaturePhoto = photo
        this.signatureDirty = false
        await this.uploadPhoto(photo)
      } finally {
        this.signing = false
      }
    },
    async clearSignature () {
      if (!this.signaturePhoto) return
      const photo = this.signaturePhoto
      this.$set(photo, 'deleting', true)
      try {
        if (photo.fileId) {
          const response = await HttpMobileFix.DeletePhoto(photo.fileId)
          if (!response || response.status !== 204) {
            this.photoError = this.photoRemoveError(response)
            return
          }
        }
        revokePhotoPreview(photo)
        this.signaturePhoto = null
        this.signatureViewerOpen = false
        this.signatureDirty = false
        this.$nextTick(this.resizeSignaturePad)
      } catch (e) {
        this.photoError = this.photoRemoveError(e && e.response)
      } finally {
        photo.deleting = false
      }
    },
    removeItemView (line) {
      return {
        Id: line.removeItemId,
        ItemId: line.removeItemId,
        Number: line.removeNumber,
        Name: line.removeName
      }
    },
    returnItemView (line) {
      return {
        Id: line.returnItemId,
        ItemId: line.returnItemId,
        Number: line.returnNumber,
        Name: line.returnName
      }
    },
    clearLineRemoveItem (line) {
      this.setLineRemoveItem(line, { ItemId: '', Number: '', Name: '', HasSerialNumber: false })
    },
    clearLineReturnItem (line) {
      this.setLineReturnItem(line, { ItemId: '', Number: '', Name: '', HasSerialNumber: false })
    },
    setLineRemoveItem (line, val) {
      this.$set(line, 'removeItemId', (val && (val.ItemId || val.Id)) || '')
      this.$set(line, 'removeNumber', (val && val.Number) || '')
      this.$set(line, 'removeName', (val && val.Name) || '')
      this.$set(line, 'removeHasSerialNumber', !!(val && val.HasSerialNumber))
    },
    setLineReturnItem (line, val) {
      this.$set(line, 'returnItemId', (val && (val.ItemId || val.Id)) || '')
      this.$set(line, 'returnNumber', (val && val.Number) || '')
      this.$set(line, 'returnName', (val && val.Name) || '')
      this.$set(line, 'returnHasSerialNumber', !!(val && val.HasSerialNumber))
    },
    applyScannedText (text) {
      const value = String(text || '').trim()
      if (this.scanTargetLine && this.scanTargetField) {
        this.$set(this.scanTargetLine, this.scanTargetField, value)
        this.lookupSerial(this.scanTargetLine, this.scanTargetField)
      }
      this.scanTargetLine = null
      this.scanTargetField = ''
    },
    async lookupSerial (line, field) {
      this.serialError = ''
      const serial = String((line && line[field]) || '').trim()
      if (!line || !serial) return
      this.serialError = ''
      try {
        const item = await this.findItemBySerial(serial)
        if (!item) {
          this.serialError = this.$t('mobile_quick_create_serial_not_found')
          return
        }
      } catch (e) {
        console.error(e)
        this.serialError = this.$t('mobile_quick_create_serial_lookup_failed')
      }
    },
    async findItemBySerial (serial) {
      const number = String(serial || '').trim()
      if (!number) return null
      try {
        const response = await HttpMobileFix.GetItemBySerial(number)
        const data =
          response && response.status === 200
            ? response.data && response.data.Data !== undefined
              ? response.data.Data
              : response.data
            : null
        if (!data || !(data.itemId || data.ItemId)) return null
        return {
          ItemId: data.itemId || data.ItemId || '',
          Id: data.itemId || data.ItemId || '',
          Number: data.number || data.Number || '',
          Name: data.name || data.Name || '',
          HasSerialNumber: data.hasSerialNumber !== false && data.HasSerialNumber !== false
        }
      } catch (e) {
        return null
      }
    },
    openLineScanner (line, field) {
      this.scanTargetLine = line
      this.scanTargetField = field
      this.openSerialScanner()
    },
    openSerialScanner () {
      this.scanError = ''
      this.scannerOpen = true
      this.$nextTick(() => this.startSerialScanner())
    },
    async startSerialScanner () {
      this.scanning = true
      this.scanError = ''
      try {
        await this.$nextTick()
        if (this.html5Qrcode) await this.stopHtml5Qrcode()
        this.html5Qrcode = new Html5Qrcode('serial-reader')
        await this.html5Qrcode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decodedText) => {
            this.applyScannedText(decodedText)
            this.closeSerialScanner()
          }
        )
      } catch (e) {
        console.error(e)
        const name = e && (e.name || e.message)
        this.scanError = this.$t(
          name && String(name).indexOf('NotFoundError') !== -1
            ? 'mobile_quick_create_scan_no_camera'
            : 'mobile_quick_create_scan_camera_denied'
        )
      } finally {
        this.scanning = false
      }
    },
    async onSerialImageSelected (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.scanError = ''
      try {
        await this.stopHtml5Qrcode()
        await this.$nextTick()
        this.html5Qrcode = new Html5Qrcode('serial-reader')
        const result = await this.html5Qrcode.scanFile(file, false)
        const text = typeof result === 'string' ? result : result && result.decodedText
        if (!text) {
          this.scanError = this.$t('mobile_quick_create_scan_image_empty')
          return
        }
        this.applyScannedText(text)
        await this.closeSerialScanner()
      } catch (e) {
        console.error(e)
        this.scanError = this.$t('mobile_quick_create_scan_image_empty')
      }
    },
    async stopHtml5Qrcode () {
      if (!this.html5Qrcode) return
      try {
        const state = this.html5Qrcode.getState && this.html5Qrcode.getState()
        if (state === 2) await this.html5Qrcode.stop()
      } catch (e) {}
      try {
        this.html5Qrcode.clear()
      } catch (e) {}
      this.html5Qrcode = null
    },
    async closeSerialScanner () {
      this.scannerOpen = false
      await this.stopHtml5Qrcode()
    },
    addWorkItemLine (kind) {
      this.workItemLines.push(createWorkItemLine(kind))
    },
    removeWorkItemLine (index) {
      this.workItemLines.splice(index, 1)
    },
    lineTitle (line, index) {
      const action =
        line.kind === 'remove'
          ? this.$t('mobile_quick_create_remove')
          : this.$t('mobile_quick_create_install')
      const name = line.kind === 'remove' ? line.removeName : line.returnName
      return index + 1 + '. ' + action + ' ' + (name || this.$t('mobile_quick_create_no_item'))
    },
    isSubmitDisabled () {
      return this.submissionBlocked || this.submitting || this.isUploading || this.isDeleting
    },
    customerItemText (item) {
      if (!item) return ''
      return item.Number + ' - ' + item.ShortName
    },
    clearSelectedCustomer () {
      this.selectedCustomer = null
      this.form.customerId = ''
      this.form.shortName = ''
      this.form.fullName = ''
    },
    onCustomerChange (customerId) {
      const selected = this.customerItems.find((item) => item.Id === customerId) || null
      this.selectedCustomer = selected
      this.form.customerId = selected ? selected.Id : ''
      this.form.shortName = selected ? selected.ShortName : ''
      this.form.fullName = selected ? selected.FullName : ''
      this.customerError = ''
    },
    async loadInitialCustomers () {
      if (shouldSearchCustomers(this.customerSearch)) return
      if (this.initialCustomersLoaded) {
        this.customers = this.initialCustomers.slice()
        return
      }
      if (this.customerLoading) return
      const sequence = ++this.customerSearchSequence
      await this.searchCustomers('', sequence, true)
    },
    async searchCustomers (search, sequence, allowEmpty = false) {
      const activeSequence = typeof sequence === 'number' ? sequence : ++this.customerSearchSequence
      const normalizedSearch = search ? String(search).trim() : ''
      if (!allowEmpty && !shouldSearchCustomers(normalizedSearch)) {
        if (activeSequence === this.customerSearchSequence) {
          this.customers = this.initialCustomersLoaded ? this.initialCustomers.slice() : []
          this.customerError = ''
          this.customerLoading = false
        }
        return
      }
      this.customerError = ''
      this.customerLoading = true
      try {
        const response = await HttpCustomer.GetCustomerForAutocompleteList(
          buildCustomerSearchParams(normalizedSearch),
          null,
          CUSTOMER_SEARCH_TIMEOUT_MS
        )
        const hasCustomerData =
          response && response.status === 200 && response.data && Array.isArray(response.data.Data)
        if (allowEmpty && !shouldSearchCustomers(normalizedSearch) && hasCustomerData) {
          this.initialCustomers = response.data.Data.slice()
          this.initialCustomersLoaded = true
        }
        if (activeSequence !== this.customerSearchSequence) return
        if (!hasCustomerData) {
          this.customers = []
          this.customerError = this.$t('mobile_quick_create_customer_load_failed')
          return
        }
        this.customers = response.data.Data
      } catch (e) {
        if (activeSequence === this.customerSearchSequence) {
          this.customers = []
          this.customerError = this.$t('mobile_quick_create_customer_load_failed')
        }
      } finally {
        if (activeSequence === this.customerSearchSequence) this.customerLoading = false
      }
    },
    openPhotoPicker () {
      this.photoError = ''
      if (this.photoLimitReached) {
        this.photoError = this.$t('mobile_quick_create_photo_limit')
        return
      }
      if (this.$refs.photoInput) this.$refs.photoInput.click()
    },
    async onPhotoSelected (event) {
      const files = event.target.files
      const file = files && files[0]
      event.target.value = ''
      if (!file) return
      if (this.photoLimitReached) {
        this.photoError = this.$t('mobile_quick_create_photo_limit')
        return
      }
      this.clearPendingPhoto()
      this.pendingPhotoFile = file
      this.pendingPhotoUrl = URL.createObjectURL(file)
      this.photoConfirmOpen = true
    },
    clearPendingPhoto () {
      if (this.pendingPhotoUrl) {
        URL.revokeObjectURL(this.pendingPhotoUrl)
      }
      this.pendingPhotoUrl = ''
      this.pendingPhotoFile = null
    },
    cancelPhotoConfirm () {
      this.photoConfirmOpen = false
      this.clearPendingPhoto()
    },
    async confirmPendingPhoto () {
      const file = this.pendingPhotoFile
      if (!file) return
      this.photoConfirmOpen = false
      const photo = createPhotoItem(file, createRequestId())
      this.photos.push(photo)
      this.clearPendingPhoto()
      await this.uploadPhoto(photo)
    },
    openPhotoViewer (photo) {
      if (!photo || !photo.previewUrl) return
      this.viewingPhoto = photo
      this.photoViewerOpen = true
    },
    async uploadPhoto (photo) {
      photo.status = PHOTO_STATUS.UPLOADING
      photo.progress = 0
      this.photoError = ''
      const isSignature = photo === this.signaturePhoto
      try {
        if (isSignature) {
          photo.uploadFile = photo.originalFile
        } else if (!photo.uploadFile) {
          const compressedFile = await compressImageFile(photo.originalFile)
          if (this._isBeingDestroyed || this._isDestroyed) return
          replacePhotoPreviewWithCompressed(photo, compressedFile)
        }
        const formData = new FormData()
        formData.append('file', photo.uploadFile)
        const response = await HttpMobileFix.UploadPhoto(formData, (event) => {
          photo.progress = uploadProgressPercent(event)
        })
        if (response && response.status === 200 && response.data && response.data.fileId) {
          photo.status = PHOTO_STATUS.SUCCESS
          photo.progress = 100
          photo.fileId = response.data.fileId
          photo.fileName = response.data.fileName
          this.photoError = ''
          return
        }
        photo.status = PHOTO_STATUS.FAILURE
        this.photoError = this.photoUploadError(response)
      } catch (e) {
        photo.status = PHOTO_STATUS.FAILURE
        this.photoError = this.photoUploadError(e && e.response)
      }
    },
    photoUploadError (response) {
      const code = response && response.data && response.data.code
      return this.$t(
        code === 'temp_storage_full'
          ? 'mobile_quick_create_error_temp_storage_full'
          : 'mobile_quick_create_photo_failed'
      )
    },
    retryPhoto (photo) {
      if (this.submissionLocked || photo.deleting) return
      return this.uploadPhoto(photo)
    },
    async removePhoto (localId) {
      const photo = this.photos.find((p) => p.localId === localId)
      if (
        !photo ||
        this.submissionLocked ||
        this.submitting ||
        photo.deleting ||
        photo.status === PHOTO_STATUS.UPLOADING
      ) {
        return
      }
      this.$set(photo, 'deleting', true)
      this.photoError = ''
      try {
        if (photo.fileId) {
          const response = await HttpMobileFix.DeletePhoto(photo.fileId)
          if (!response || response.status !== 204) {
            this.photoError = this.photoRemoveError(response)
            return
          }
        }
        const index = this.photos.indexOf(photo)
        if (index !== -1) {
          revokePhotoPreview(photo)
          this.photos.splice(index, 1)
        }
      } catch (e) {
        this.photoError = this.photoRemoveError(e && e.response)
      } finally {
        photo.deleting = false
      }
    },
    photoRemoveError (response) {
      const code = response && response.data && response.data.code
      return this.$t(
        code === 'request_in_progress'
          ? 'mobile_quick_create_photo_in_use'
          : 'mobile_quick_create_photo_remove_failed'
      )
    },
    invalidateUploadedPhotos () {
      this.photos.forEach((photo) => {
        if (photo.status !== PHOTO_STATUS.SUCCESS) return
        photo.status = PHOTO_STATUS.FAILURE
        photo.progress = 0
        photo.fileId = null
        photo.fileName = null
      })
      if (this.signaturePhoto && this.signaturePhoto.status === PHOTO_STATUS.SUCCESS) {
        this.signaturePhoto.status = PHOTO_STATUS.FAILURE
        this.signaturePhoto.progress = 0
        this.signaturePhoto.fileId = null
        this.signaturePhoto.fileName = null
      }
      this.photoError = this.$t('mobile_quick_create_error_temp_photo_expired')
    },
    resetForm () {
      this.closeSerialScanner()
      this.photos.forEach(revokePhotoPreview)
      if (this.signaturePhoto) revokePhotoPreview(this.signaturePhoto)
      this.clearPendingPhoto()
      this.form = emptyQuickCreateForm()
      this.workOrderOpen = true
      this.workItemsOpen = false
      this.signatureOpen = false
      this.signaturePhoto = null
      this.signatureViewerOpen = false
      this.signing = false
      this.drawing = false
      this.signatureDirty = false
      this.lastPoint = null
      this.workItemLines = []
      this.customers = this.initialCustomersLoaded ? this.initialCustomers.slice() : []
      this.selectedCustomer = null
      this.customerSearch = ''
      this.customerError = ''
      this.customerSearchSequence++
      this.photos = []
      this.photoError = ''
      this.photoConfirmOpen = false
      this.photoViewerOpen = false
      this.viewingPhoto = null
      if (this.$refs.observer) this.$refs.observer.reset()
    },
    collectFileIds () {
      const ids = successfulFileIds(this.photos).slice()
      if (
        this.signaturePhoto &&
        this.signaturePhoto.status === PHOTO_STATUS.SUCCESS &&
        this.signaturePhoto.fileId
      ) {
        ids.push(this.signaturePhoto.fileId)
      }
      return ids
    },
    async submit () {
      if (this.missingLabels.length) {
        this.workOrderOpen = true
        this.photoError = this.missingLabels.some((item) => item.key === 'photo')
          ? this.$t('mobile_quick_create_photo_required')
          : ''
        if (this.$refs.observer) await this.$refs.observer.validate()
        const first = this.missingLabels[0]
        if (first) this.focusMissing(first.key)
        return
      }
      if (this.submitting || this.submissionBlocked || this.isUploading || this.isDeleting) return
      this.photoError = ''
      const valid = await this.$refs.observer.validate()
      if (
        !valid ||
        this.submitting ||
        this.submissionBlocked ||
        this.isUploading ||
        this.isDeleting
      ) {
        return
      }
      if (!this.selectedCustomer || this.form.customerId !== this.selectedCustomer.Id) {
        this.clearSelectedCustomer()
        this.customerError = this.$t('mobile_quick_create_customer_reselect')
        return
      }
      if (!hasSuccessfulPhoto(this.photos)) {
        this.photoError = this.$t('mobile_quick_create_photo_required')
        return
      }
      this.$emit(
        'submit',
        Object.assign({}, this.form, {
          fileIds: this.collectFileIds(),
          engineerWorkItems: buildEngineerWorkItems(this.workItemLines)
        })
      )
    }
  }
}
</script>

<style scoped>
.mobile-quick-create-form {
  --mobile-fix-ink: #102a43;
  --mobile-fix-muted: #526477;
  --mobile-fix-line: #cbd6df;
  --mobile-fix-accent: #0b6fbd;
  --mobile-fix-action: #0f766e;
  --mobile-fix-disabled-bg: #dce7e5;
  --mobile-fix-disabled-ink: #405a55;
  padding-bottom: calc(148px + env(safe-area-inset-bottom, 0px));
  background: #ffffff;
  color: var(--mobile-fix-ink);
}
.mobile-quick-create-form__section {
  padding: 18px 14px;
  border-bottom: 10px solid #eef2f3;
  background: #ffffff;
}
.mobile-quick-create-form__section:last-of-type {
  border-bottom: 0;
}
.mobile-quick-create-form__section-heading {
  display: flex;
  min-height: 32px;
  margin-bottom: 16px;
  align-items: center;
}
.mobile-quick-create-form__section-toggle {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
}
.mobile-quick-create-form__section-icon {
  display: inline-flex;
  width: 32px;
  height: 32px;
  margin-right: 10px;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
}
.mobile-quick-create-form__section-icon--work {
  background: #e5f4f2;
}
.mobile-quick-create-form__section-icon--photo {
  background: #fff1df;
}
.mobile-quick-create-form__section-title {
  margin: 0;
  color: var(--mobile-fix-ink);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
}
.mobile-quick-create-form__field-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px 12px;
  margin-bottom: 12px;
}
.mobile-quick-create-form__control {
  margin-top: 0;
}
.mobile-quick-create-form
  ::v-deep
  .mobile-quick-create-form__control
  > .v-input__control
  > .v-input__slot {
  min-height: 52px;
  border-radius: 7px;
  background: #ffffff !important;
  box-shadow: none !important;
}
.mobile-quick-create-form ::v-deep .mobile-quick-create-form__control fieldset {
  border-color: var(--mobile-fix-line);
}
.mobile-quick-create-form ::v-deep .mobile-quick-create-form__control.v-input--is-focused fieldset {
  border-color: var(--mobile-fix-accent) !important;
}
.mobile-quick-create-form ::v-deep .mobile-quick-create-form__control.error--text fieldset {
  border-color: #b42318 !important;
}
.mobile-quick-create-form ::v-deep .mobile-quick-create-form__control .v-label {
  color: var(--mobile-fix-muted);
}
.mobile-quick-create-form
  ::v-deep
  .mobile-quick-create-form__control
  .v-input__icon--append
  .v-icon {
  color: var(--mobile-fix-action);
}
.mobile-quick-create-form__customer-summary {
  margin: -2px 0 14px;
  padding: 11px 12px;
  border: 1px solid #badbd7;
  border-left: 4px solid var(--mobile-fix-action);
  border-radius: 7px;
  background: #f2faf9;
}
.mobile-quick-create-form__customer-heading {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
}
.mobile-quick-create-form__customer-name {
  margin-top: 2px;
  color: var(--mobile-fix-muted);
  font-size: 0.875rem;
}
.mobile-quick-create-form__item-card {
  margin-bottom: 10px;
  border: 1px solid var(--mobile-fix-line);
  border-radius: 8px;
  background: #fafcfd;
}
.mobile-quick-create-form__item-card-head {
  display: flex;
  width: 100%;
  min-height: 48px;
  padding: 0 12px;
  align-items: center;
  justify-content: space-between;
  border: 0;
  background: transparent;
  color: var(--mobile-fix-ink);
  font-weight: 700;
}
.mobile-quick-create-form__item-card-body {
  padding: 0 12px 12px;
}
.mobile-quick-create-form__item-label {
  margin: 8px 0 4px;
  color: var(--mobile-fix-action);
  font-size: 0.8rem;
  font-weight: 700;
}
.mobile-quick-create-form__item-actions {
  display: flex;
  gap: 8px;
}
.mobile-quick-create-form__signature-pad {
  width: 100%;
  height: 180px;
  border: 1px dashed #79b8b2;
  border-radius: 8px;
  background: #fff;
  touch-action: none;
}
.mobile-quick-create-form__signature-preview img {
  width: 100%;
  height: 140px;
  object-fit: contain;
  border: 1px solid #cbd6df;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}
.mobile-quick-create-form__signature-status {
  margin-top: 6px;
  font-size: 0.875rem;
}
.mobile-quick-create-form__signature-full {
  width: 100%;
  background: #fff;
}
.mobile-quick-create-form__scanner-video {
  width: 100%;
  min-height: 220px;
  border-radius: 8px;
  background: #000;
}
.mobile-quick-create-form__optional-toggle {
  min-height: 44px;
  margin-left: -8px;
  color: #075985;
  font-weight: 700;
  text-transform: none;
}
.mobile-quick-create-form__photos {
  padding-bottom: 20px;
}
.mobile-quick-create-form__photo-count {
  margin-left: auto;
  color: var(--mobile-fix-muted);
  font-size: 0.8rem;
  font-weight: 600;
}
.mobile-quick-create-form__photo-list {
  background: transparent;
}
.mobile-quick-create-form ::v-deep .mobile-quick-create-form__photo-list .v-list-item {
  min-height: 68px;
  margin-bottom: 8px;
  padding: 6px 8px !important;
  border: 1px solid var(--mobile-fix-line);
  border-radius: 6px;
  background: #fafcfd;
}
.mobile-quick-create-form__photo-thumb {
  cursor: pointer;
}
.mobile-quick-create-form__photo-button {
  min-height: 48px;
  border: 1px solid #98c9c4;
  border-radius: 8px;
  background: #eef9f7 !important;
  color: #0f6b64 !important;
  font-weight: 700;
  text-transform: none;
}
.mobile-quick-create-form__photo-button--empty {
  min-height: 96px;
  border: 1px dashed #79b8b2;
  background: #f4fbfa !important;
}
.mobile-quick-create-form ::v-deep .mobile-quick-create-form__photo-button--empty .v-btn__content {
  flex-direction: column;
}
.mobile-quick-create-form__actions {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4;
  border-top: 1px solid #d4dde3;
  background: #ffffff;
  padding: 10px 14px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
}
.mobile-quick-create-form__actions-inner {
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
}
.mobile-quick-create-form__missing {
  margin-bottom: 8px;
  padding: 8px 10px;
  border: 1px solid #f3c7c2;
  border-radius: 8px;
  background: #fff6f5;
  text-align: center;
}
.mobile-quick-create-form__missing-title {
  color: #b42318;
  font-size: 0.8rem;
  font-weight: 700;
}
.mobile-quick-create-form__missing-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 6px;
}
.mobile-quick-create-form__missing-chip {
  padding: 4px 8px;
  border-radius: 999px;
  background: #ffffff;
  color: #b42318;
  font-size: 0.75rem;
  font-weight: 700;
}
.mobile-quick-create-form__submit {
  min-height: 52px;
  border-radius: 8px;
  font-weight: 700;
  text-transform: none;
}
.mobile-quick-create-form
  ::v-deep
  .mobile-quick-create-form__submit.v-btn.v-btn--disabled.v-btn--has-bg {
  background-color: var(--mobile-fix-disabled-bg) !important;
  color: var(--mobile-fix-disabled-ink) !important;
  opacity: 1;
}
.mobile-quick-create-form__item-card-head--remove {
  background: #fff6f5;
  color: #b42318;
}
.mobile-quick-create-form__item-card-head--install {
  background: #f2faf9;
  color: #0f766e;
}
.mobile-quick-create-form ::v-deep .v-autocomplete .v-label > .red--text {
  display: none !important;
}
.mobile-quick-create-form__hint {
  margin: -8px 0 8px 42px;
  color: var(--mobile-fix-muted);
  font-size: 0.75rem;
  line-height: 1.3;
}
@media (min-width: 360px) {
  .mobile-quick-create-form__field-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 681px) {
  .mobile-quick-create-form__section {
    padding-right: 20px;
    padding-left: 20px;
  }
}
</style>
